import { useState, useEffect } from "react";
import { X, Building, User, Loader2, Users, CheckCircle, FileText, Phone, Mail } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Card, CardContent } from "../../../../components/ui/card";
import { useAuth } from "../../../../context/AuthContext";
import Swal from "sweetalert2";
import { obtenerTorres, Torre } from "../../../../api/torreService";
import { obtenerApartamentos, Apartamento } from "../../../../api/apartamentoService";
import { 
  obtenerResidentes, 
  obtenerResidenteDetalle,
  ingresarResidenteALibroVisitas,
  Residente 
} from "../../../../api/residenteService";

interface SearchResidentModalProps {
  isOpen: boolean;
  onClose: (success?: boolean) => void;
}

export default function SearchResidentModal({ isOpen, onClose }: SearchResidentModalProps) {
  const { session } = useAuth();
  const unidadId = session?.unidad?.id || "";
  const userEmail = session?.usuario?.email || session?.email || "";
  
  // Estados de selección
  const [selectedTorre, setSelectedTorre] = useState("");
  const [selectedApartamento, setSelectedApartamento] = useState("");
  const [selectedResidente, setSelectedResidente] = useState<Residente | null>(null);
  
  // Estados de catálogos
  const [torres, setTorres] = useState<Torre[]>([]);
  const [apartamentos, setApartamentos] = useState<Apartamento[]>([]);
  const [residentes, setResidentes] = useState<Residente[]>([]);
  
  // Estados de carga
  const [isLoadingTorres, setIsLoadingTorres] = useState(false);
  const [isLoadingApartamentos, setIsLoadingApartamentos] = useState(false);
  const [isLoadingResidentes, setIsLoadingResidentes] = useState(false);
  const [isLoadingDetalle, setIsLoadingDetalle] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Cargar torres al abrir el modal
  useEffect(() => {
    if (isOpen && unidadId) {
      loadTorres();
      // Resetear estados
      setSelectedTorre("");
      setSelectedApartamento("");
      setSelectedResidente(null);
      setApartamentos([]);
      setResidentes([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, unidadId]);

  // Cargar apartamentos cuando se selecciona una torre
  useEffect(() => {
    if (selectedTorre && unidadId) {
      loadApartamentos(selectedTorre);
      setSelectedApartamento("");
      setResidentes([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTorre]);

  // Cargar residentes cuando se selecciona un apartamento
  useEffect(() => {
    if (selectedApartamento && unidadId) {
      loadResidentes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedApartamento, selectedTorre]);

  const loadTorres = async () => {
    if (!unidadId) return;
    setIsLoadingTorres(true);
    try {
      const response = await obtenerTorres({
        "unidad residencial codigo": unidadId,
      });
      if (response.code === "1" && response.object_) {
        setTorres(response.object_);
      } else {
        setTorres([]);
      }
    } catch (error) {
      console.error("Error al cargar torres:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al cargar las torres. Por favor, intenta nuevamente.",
        confirmButtonColor: "#1a3c34",
      });
      setTorres([]);
    } finally {
      setIsLoadingTorres(false);
    }
  };

  const loadApartamentos = async (torreCodigo: string) => {
    if (!unidadId || !torreCodigo) return;
    setIsLoadingApartamentos(true);
    try {
      const response = await obtenerApartamentos({
        "unidad residencial codigo": unidadId,
        "torre codigo": torreCodigo,
      });
      if (response.code === "1" && response.object_) {
        setApartamentos(response.object_);
      } else {
        setApartamentos([]);
      }
    } catch (error) {
      console.error("Error al cargar apartamentos:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al cargar los apartamentos. Por favor, intenta nuevamente.",
        confirmButtonColor: "#1a3c34",
      });
      setApartamentos([]);
    } finally {
      setIsLoadingApartamentos(false);
    }
  };

  const loadResidentes = async () => {
    if (!unidadId || !selectedApartamento) return;
    setIsLoadingResidentes(true);
    try {
      const response = await obtenerResidentes({
        "correo electronico usuario": "",
        "unidad residencial codigo": unidadId,
        "torre codigo": selectedTorre || "",
        "apartamento codigo": selectedApartamento || "",
        "tipo usuario codigo": "",
      });
      if (response.code === "1" && response.object_) {
        setResidentes(response.object_);
      } else {
        setResidentes([]);
      }
    } catch (error) {
      console.error("Error al cargar residentes:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al cargar los residentes. Por favor, intenta nuevamente.",
        confirmButtonColor: "#1a3c34",
      });
      setResidentes([]);
    } finally {
      setIsLoadingResidentes(false);
    }
  };

  const handleSelectResidente = async (residente: Residente) => {
    if (!residente.res_email || !selectedApartamento) return;
    setIsLoadingDetalle(true);
    try {
      const response = await obtenerResidenteDetalle({
        "correo electronico usuario": "",
        "unidad residencial codigo": unidadId,
        "torre codigo": selectedTorre || "",
        "apartamento codigo": selectedApartamento || "",
        "tipo usuario codigo": "",
      });
      
      if (response.code === "1" && response.object_ && response.object_.length > 0) {
        // Tomar el primer resultado que coincida con el email
        const detalleResidente = response.object_.find(
          (r: Residente) => r.res_email === residente.res_email
        ) || response.object_[0];
        setSelectedResidente(detalleResidente);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Error al cargar el detalle del residente.",
          confirmButtonColor: "#1a3c34",
        });
      }
    } catch (error) {
      console.error("Error al cargar detalle:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al cargar el detalle del residente.",
        confirmButtonColor: "#1a3c34",
      });
    } finally {
      setIsLoadingDetalle(false);
    }
  };

  const handleIngresarALibro = async () => {
    if (!selectedResidente || !selectedTorre || !selectedApartamento) return;

    const torreNombre = torres.find(t => t.tor_codigo === selectedTorre)?.tor_nombre || "";
    const apartamentoNombre = apartamentos.find(a => a.apt_codigo === selectedApartamento)?.apt_nombre || "";

    if (!torreNombre || !apartamentoNombre) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al obtener la información de la torre o apartamento.",
        confirmButtonColor: "#1a3c34",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        unidad_residencial: unidadId,
        "torre nombre": torreNombre,
        "apartamento nombre": apartamentoNombre,
        nombre: selectedResidente.res_nombre_completo || selectedResidente.res_nombre || "",
        tipo_documento_codigo: selectedResidente.res_tipo_documento || "",
        numero_documento: selectedResidente.res_numero_documento || "",
        celular: selectedResidente.res_numero_celular || selectedResidente.res_celular || "",
        "foto nombre": selectedResidente.res_ruta_foto || selectedResidente.res_foto || "",
      };

      const response = await ingresarResidenteALibroVisitas(payload);

      if (response.code === "1" || response.code === 1) {
        await Swal.fire({
          icon: "success",
          title: "¡Éxito!",
          text: response.message || "El residente ha sido ingresado al libro de visitas exitosamente.",
          confirmButtonColor: "#1a3c34",
        });
        onClose(true);
      } else {
        throw new Error(response.message || "Error al ingresar el residente");
      }
    } catch (error) {
      console.error("Error al ingresar residente:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error instanceof Error ? error.message : "Error al ingresar el residente al libro de visitas.",
        confirmButtonColor: "#1a3c34",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getImageUrl = (rutaFoto?: string) => {
    if (!rutaFoto || rutaFoto === "no_hay_imagen.png") {
      return null;
    }
    // Si la URL ya es completa (empieza con http), usarla directamente
    if (rutaFoto.startsWith('http://') || rutaFoto.startsWith('https://')) {
      return rutaFoto;
    }
    // Si es una ruta relativa, construir la URL completa
    const apiBaseUrl = process.env.REACT_APP_API_PROD || 'https://api.easyph.com.co/api/';
    const baseUrl = apiBaseUrl.replace(/\/api\/?$/, '');
    return `${baseUrl}/${rutaFoto}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <h3 className="text-lg sm:text-xl font-bold text-military-600 flex items-center gap-2">
            <User className="h-5 w-5" />
            Buscar Residente
          </h3>
          <button
            onClick={() => onClose()}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold transition"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Selección de Torre */}
          <div className="space-y-2">
            <label className="text-sm sm:text-base font-semibold text-gray-700 flex items-center gap-2">
              <Building className="h-4 w-4" />
              Torre
            </label>
            <select
              value={selectedTorre}
              onChange={(e) => setSelectedTorre(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm sm:text-base bg-white focus:outline-none focus:ring-2 focus:ring-military-500"
              disabled={isLoadingTorres}
            >
              <option value="">Seleccione una torre</option>
              {torres.map((torre) => (
                <option key={torre.tor_codigo} value={torre.tor_codigo}>
                  {torre.tor_nombre}
                </option>
              ))}
            </select>
          </div>

          {/* Selección de Apartamento */}
          {selectedTorre && (
            <div className="space-y-2">
              <label className="text-sm sm:text-base font-semibold text-gray-700 flex items-center gap-2">
                <Building className="h-4 w-4" />
                Apartamento
              </label>
              <select
                value={selectedApartamento}
                onChange={(e) => setSelectedApartamento(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm sm:text-base bg-white focus:outline-none focus:ring-2 focus:ring-military-500"
                disabled={isLoadingApartamentos || !selectedTorre}
              >
                <option value="">Seleccione un apartamento</option>
                {apartamentos.map((apt) => (
                  <option key={apt.apt_codigo} value={apt.apt_codigo}>
                    {apt.apt_nombre}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Lista de Residentes */}
          {selectedApartamento && (
            <div className="space-y-3">
              <h4 className="text-base sm:text-lg font-semibold text-gray-700 flex items-center gap-2">
                <Users className="h-4 w-4 sm:h-5 sm:w-5" />
                Residentes
              </h4>
              {isLoadingResidentes ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-military-500" />
                </div>
              ) : residentes.length === 0 ? (
                <p className="text-gray-600 text-center py-4 text-sm sm:text-base">
                  No hay residentes registrados para este apartamento.
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {residentes.map((residente, index) => (
                    <Card
                      key={residente.res_email || `residente-${index}`}
                      className={`cursor-pointer transition hover:shadow-md ${
                        selectedResidente?.res_email === residente.res_email
                          ? "border-2 border-military-500 bg-military-50"
                          : "border border-gray-200"
                      }`}
                      onClick={() => handleSelectResidente(residente)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0">
                            {(() => {
                              const imageUrl = getImageUrl(residente.res_ruta_foto || residente.res_foto);
                              return imageUrl ? (
                                <img
                                  src={imageUrl}
                                  alt={residente.res_nombre_completo || residente.res_nombre}
                                  className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover border-2 border-military-500"
                                />
                              ) : (
                                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gray-200 flex items-center justify-center border-2 border-military-500">
                                  <User className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400" />
                                </div>
                              );
                            })()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h5 className="font-semibold text-sm sm:text-base text-gray-900 truncate">
                              {residente.res_nombre_completo || residente.res_nombre || "Sin nombre"}
                            </h5>
                            {residente.res_email && (
                              <p className="text-xs sm:text-sm text-gray-600 mt-1 truncate">
                                {residente.res_email}
                              </p>
                            )}
                            {residente.res_tipo_usuario_nombre && (
                              <p className="text-xs sm:text-sm text-gray-500 mt-1">
                                {residente.res_tipo_usuario_nombre}
                              </p>
                            )}
                            {(residente.res_numero_celular || residente.res_celular) && (
                              <p className="text-xs sm:text-sm text-gray-600 mt-1">
                                {residente.res_numero_celular || residente.res_celular}
                              </p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Detalle del Residente Seleccionado */}
          {selectedResidente && (
            <Card className="border-2 border-military-500 bg-military-50">
              <CardContent className="p-4 sm:p-6">
                <h4 className="text-base sm:text-lg font-bold text-military-600 mb-4 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5" />
                  Información del Residente
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-center">
                    {(() => {
                      const imageUrl = getImageUrl(selectedResidente.res_ruta_foto || selectedResidente.res_foto);
                      return imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={selectedResidente.res_nombre_completo || selectedResidente.res_nombre}
                          className="w-24 h-24 sm:w-32 sm:h-32 rounded-full object-cover border-4 border-military-500"
                        />
                      ) : (
                        <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gray-200 flex items-center justify-center border-4 border-military-500">
                          <User className="h-12 w-12 sm:h-16 sm:w-16 text-gray-400" />
                        </div>
                      );
                    })()}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm sm:text-base">
                    <div>
                      <p className="font-semibold text-gray-700 flex items-center gap-2">
                        <User className="h-4 w-4" />
                        Nombre:
                      </p>
                      <p className="text-gray-900">{selectedResidente.res_nombre_completo || selectedResidente.res_nombre || "No especificado"}</p>
                    </div>
                    {selectedResidente.res_email && (
                      <div>
                        <p className="font-semibold text-gray-700 flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          Email:
                        </p>
                        <p className="text-gray-900">{selectedResidente.res_email}</p>
                      </div>
                    )}
                    {selectedResidente.res_tipo_usuario_nombre && (
                      <div>
                        <p className="font-semibold text-gray-700">Tipo Usuario:</p>
                        <p className="text-gray-900">{selectedResidente.res_tipo_usuario_nombre}</p>
                      </div>
                    )}
                    {selectedResidente.res_tipo_documento_nombre && (
                      <div>
                        <p className="font-semibold text-gray-700">Tipo Documento:</p>
                        <p className="text-gray-900">{selectedResidente.res_tipo_documento_nombre}</p>
                      </div>
                    )}
                    {selectedResidente.res_numero_documento && (
                      <div>
                        <p className="font-semibold text-gray-700">Número Documento:</p>
                        <p className="text-gray-900">{selectedResidente.res_numero_documento}</p>
                      </div>
                    )}
                    {(selectedResidente.res_numero_celular || selectedResidente.res_celular) && (
                      <div>
                        <p className="font-semibold text-gray-700 flex items-center gap-2">
                          <Phone className="h-4 w-4" />
                          Celular:
                        </p>
                        <p className="text-gray-900">{selectedResidente.res_numero_celular || selectedResidente.res_celular}</p>
                      </div>
                    )}
                    {selectedResidente.res_apartamento_nombre && (
                      <div>
                        <p className="font-semibold text-gray-700 flex items-center gap-2">
                          <Building className="h-4 w-4" />
                          Ubicación:
                        </p>
                        <p className="text-gray-900">
                          Torre {selectedResidente.res_torre_nombre} - Apt {selectedResidente.res_apartamento_nombre}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 sm:p-6 flex flex-col sm:flex-row gap-3 justify-end">
          <Button
            onClick={() => onClose()}
            variant="outline"
            className="w-full sm:w-auto"
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          {selectedResidente && (
            <Button
              onClick={handleIngresarALibro}
              className="w-full sm:w-auto bg-military-500 hover:bg-military-600 text-white"
              disabled={isSubmitting || isLoadingDetalle}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Ingresando...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Ingresar al Libro de Visitantes
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

